import { getAttachmentDisplayLabel, type MessageAttachmentResponse } from '@/entities/conversations';

/** Часть простого текста сообщения: текст либо вложение, на которое текст ссылается меткой */
export type MessageTextSegment =
  | { kind: 'text'; text: string }
  | { kind: 'attachment'; attachment: MessageAttachmentResponse };

/** Вложения сообщения по внешнему идентификатору — им на вложение ссылается метка `ATTACHMENT:{id}` */
export type AttachmentsByExternalId = Map<string, MessageAttachmentResponse>;

/** Метка вложения в тексте сообщения */
const ATTACHMENT_TOKEN = /ATTACHMENT:(\d+)/g;

/** Метка вложения в подписи картинки оформленного содержимого */
const ATTACHMENT_TOKEN_IN_ALT = /ATTACHMENT:(\d+)/;

/** Поля сырых данных вложения, в которых внешняя система передаёт его тип */
const CONTENT_TYPE_KEYS = ['content_type', 'contentType', 'mime_type', 'mimeType'];

/** Расширения изображений — по ним узнаётся картинка, тип которой не передан */
const IMAGE_EXTENSION = /\.(png|jpe?g|gif|webp|bmp|svg|avif)(\?|#|$)/i;

/** Вложение — изображение: по переданному типу, а без него — по расширению имени или адреса */
export const isImageAttachment = (attachment: MessageAttachmentResponse): boolean => {
  const rawData = attachment.externalRawData;
  const contentType = CONTENT_TYPE_KEYS.map((key) => rawData?.[key]).find(
    (value): value is string => typeof value === 'string' && value.trim() !== '',
  );
  if (contentType) return contentType.toLowerCase().startsWith('image/');
  return IMAGE_EXTENSION.test(getAttachmentDisplayLabel(attachment)) || IMAGE_EXTENSION.test(attachment.url ?? '');
};

/** Вложения сообщения по внешнему идентификатору */
export const groupAttachmentsByExternalId = (attachments: MessageAttachmentResponse[]): AttachmentsByExternalId => {
  const byExternalId: AttachmentsByExternalId = new Map();
  for (const attachment of attachments) {
    if (attachment.externalId != null) byExternalId.set(String(attachment.externalId), attachment);
  }
  return byExternalId;
};

/** Внешний идентификатор вложения из подписи картинки; метки нет — `null` */
export const getAttachmentExternalIdFromAlt = (alt: string | null): string | null =>
  alt?.match(ATTACHMENT_TOKEN_IN_ALT)?.[1] ?? null;

/**
 * Делит простой текст по меткам `ATTACHMENT:{id}`: метка заменяется вложением с тем же внешним
 * идентификатором, а метка, вложения к которой нет, из текста убирается.
 *
 * @returns части текста и внешние идентификаторы вложений, показанных на месте меток
 */
export const splitTextByAttachments = (
  text: string,
  byExternalId: AttachmentsByExternalId,
): { segments: MessageTextSegment[]; placedExternalIds: Set<string> } => {
  const segments: MessageTextSegment[] = [];
  const placedExternalIds = new Set<string>();
  let lastIndex = 0;

  for (const match of text.matchAll(ATTACHMENT_TOKEN)) {
    const [token, externalId] = match;
    const index = match.index ?? 0;
    if (index > lastIndex) segments.push({ kind: 'text', text: text.slice(lastIndex, index) });

    const attachment = byExternalId.get(externalId);
    if (attachment) {
      segments.push({ kind: 'attachment', attachment });
      placedExternalIds.add(externalId);
    }
    lastIndex = index + token.length;
  }

  if (lastIndex < text.length) segments.push({ kind: 'text', text: text.slice(lastIndex) });
  return { segments, placedExternalIds };
};

/** Вложения, не показанные на месте меток, — их облако показывает под содержимым */
export const getUnplacedAttachments = (
  attachments: MessageAttachmentResponse[],
  placedExternalIds: Set<string>,
): MessageAttachmentResponse[] =>
  attachments.filter(
    (attachment) => attachment.externalId == null || !placedExternalIds.has(String(attachment.externalId)),
  );

/** Внешние идентификаторы вложений, которые оформленное содержимое показывает на месте своих картинок */
export const collectContentAttachmentExternalIds = (
  root: Element,
  byExternalId: AttachmentsByExternalId,
): Set<string> => {
  const externalIds = new Set<string>();
  for (const image of Array.from(root.getElementsByTagName('img'))) {
    const externalId = getAttachmentExternalIdFromAlt(image.getAttribute('alt'));
    if (externalId != null && byExternalId.has(externalId)) externalIds.add(externalId);
  }
  return externalIds;
};
